import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Check, X, Loader2, Trophy, Lightbulb, Hourglass, Clock } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface Question {
  question_id: string;
  question: string;
  option_a: string; option_b: string; option_c: string; option_d: string;
}
interface Scenario {
  id: string;
  topic: string;
  scenario_text: string;
  option_a: string; option_b: string; option_c: string; option_d: string;
  why_best: string;
}
interface Challenge {
  id: string;
  challenger_id: string;
  challenged_id: string;
  status: string;
  challenge_type: string;
  expires_at: string | null;
  challenger_score: number;
  challenged_score: number;
  winner_id: string | null;
  challenger_completed_at: string | null;
  challenged_completed_at: string | null;
  result_state: string | null;
  finalized_at: string | null;
}

const QUESTION_TYPES = ["quiz_duel", "topic_battle", "daily_duel", "speed_round", "accuracy"];

const ChallengeDuel = () => {
  const { challengeId } = useParams<{ challengeId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [answeredIds, setAnsweredIds] = useState<Set<string>>(new Set());
  const [currentIdx, setCurrentIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState<{ correct: boolean; bestAnswer?: string; whyBest?: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);

  const isScenario = challenge?.challenge_type === "scenario_showdown";

  const load = async () => {
    if (!challengeId) {
      setLoading(false);
      setRecoveryError("This challenge link is invalid.");
      return;
    }
    // Wait for auth to resolve before deciding the route is broken.
    if (authLoading) return;
    if (!user) {
      // Auth resolved but no user — bounce to login, preserving intent.
      navigate(`/login?redirect=${encodeURIComponent(`/challenge/${challengeId}`)}`, { replace: true });
      return;
    }
    setLoading(true);
    setRecoveryError(null);
    const { data: c, error } = await supabase
      .from("friend_challenges").select("*").eq("id", challengeId).maybeSingle();
    if (error) {
      // Network/RLS hiccup — don't dump to a 404. Show a recovery card.
      setRecoveryError("We couldn't load this challenge right now. It may have expired or moved.");
      setLoading(false);
      return;
    }
    if (!c) {
      toast.info("This challenge isn't available anymore. Returning to Challenge Center.");
      navigate("/challenges", { replace: true });
      return;
    }
    setChallenge(c);
    const ch = c as Challenge;

    if (ch.challenge_type === "scenario_showdown") {
      const { data: srows } = await supabase
        .from("friend_challenge_scenarios")
        .select("position, scenario_id, challenge_scenarios(id, topic, scenario_text, option_a, option_b, option_c, option_d, why_best)")
        .eq("challenge_id", challengeId)
        .order("position", { ascending: true });
      const items = ((srows) || []).map(r => r.challenge_scenarios).filter(Boolean);
      setScenarios(items);
      const { data: subs } = await supabase
        .from("friend_challenge_scenario_submissions")
        .select("scenario_id").eq("challenge_id", challengeId).eq("user_id", user.id);
      const seen = new Set<string>(((subs) || []).map(s => s.scenario_id));
      setAnsweredIds(seen);
      const nextIdx = items.findIndex(s => !seen.has(s.id));
      setCurrentIdx(nextIdx >= 0 ? nextIdx : items.length);
    } else if (QUESTION_TYPES.includes(ch.challenge_type)) {
      const { data: qrows } = await supabase
        .from("friend_challenge_questions")
        .select("position, question_id, questions(question_id, question, option_a, option_b, option_c, option_d)")
        .eq("challenge_id", challengeId)
        .order("position", { ascending: true });
      const items = ((qrows) || []).map(r => r.questions).filter(Boolean);
      setQuestions(items);
      const { data: subs } = await supabase
        .from("friend_challenge_submissions")
        .select("question_id").eq("challenge_id", challengeId).eq("user_id", user.id);
      const seen = new Set<string>(((subs) || []).map(s => s.question_id));
      setAnsweredIds(seen);
      const nextIdx = items.findIndex(q => !seen.has(q.question_id));
      setCurrentIdx(nextIdx >= 0 ? nextIdx : items.length);
    }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  useEffect(() => { load(); }, [challengeId, user, authLoading]);

  // When the tab becomes visible again after backgrounding, refresh challenge
  // state silently. Avoids stale "your turn" UI when the opponent just finished
  // — and prevents the page feeling broken after returning from another tab.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && user && challengeId) {
        void load();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, challengeId]);

  const submitQuestionAnswer = async (selected: string) => {
    if (!challenge || submitting) return;
    const q = questions[currentIdx];
    if (!q) return;
    setSubmitting(true);
    const { data, error } = await supabase.rpc("submit_challenge_answer", {
      p_challenge_id: challenge.id, p_question_id: q.question_id, p_selected: selected,
    });
    setSubmitting(false);
    if (error) { toast.error(error.message); return; }
    const result = data as { is_correct: boolean; completed: boolean };
    setLastResult({ correct: result.is_correct });
    setAnsweredIds(prev => new Set(prev).add(q.question_id));
    setTimeout(() => {
      setLastResult(null);
      if (result.completed) load();
      else setCurrentIdx(i => i + 1);
    }, 1400);
  };

  const submitScenarioAnswer = async (selected: string) => {
    if (!challenge || submitting) return;
    const s = scenarios[currentIdx];
    if (!s) return;
    setSubmitting(true);
    const { data, error } = await supabase.rpc("submit_scenario_answer", {
      p_challenge_id: challenge.id, p_scenario_id: s.id, p_selected: selected,
    });
    setSubmitting(false);
    if (error) { toast.error(error.message); return; }
    const result = data as { is_correct: boolean; best_answer: string; completed: boolean };
    setLastResult({ correct: result.is_correct, bestAnswer: result.best_answer, whyBest: s.why_best });
    setAnsweredIds(prev => new Set(prev).add(s.id));
    setTimeout(() => {
      setLastResult(null);
      if (result.completed) load();
      else setCurrentIdx(i => i + 1);
    }, 3500);
  };

  if (loading || authLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="w-6 h-6 animate-spin text-primary" />
    </div>;
  }
  if (recoveryError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="glass-card p-8 text-center max-w-md w-full space-y-4">
          <Clock className="w-10 h-10 text-muted-foreground mx-auto" />
          <h2 className="font-heading font-bold text-xl text-foreground">Challenge Unavailable</h2>
          <p className="text-sm text-muted-foreground">{recoveryError}</p>
          <div className="flex gap-2 justify-center">
            <Button variant="outline" onClick={() => load()}>Try Again</Button>
            <Button onClick={() => navigate("/challenges", { replace: true })}>Back to Challenge Center</Button>
          </div>
        </div>
      </div>
    );
  }
  if (!challenge || !user) return null;

  const isChallenger = challenge.challenger_id === user.id;
  const myCompletedAt = isChallenger ? challenge.challenger_completed_at : challenge.challenged_completed_at;
  const myDone = !!myCompletedAt;
  const finalState = challenge.status === "completed" || challenge.status === "expired";
  const isPendingAcceptance = challenge.status === "pending";
  const declinedOrCanceled = challenge.status === "declined" || challenge.status === "canceled";
  const isTie = challenge.result_state === "tie" || (finalState && !challenge.winner_id && challenge.result_state !== "expired");
  const isExpired = challenge.result_state === "expired";
  const iWon = !!challenge.winner_id && challenge.winner_id === user.id;
  const iLost = !!challenge.winner_id && challenge.winner_id !== user.id;
  const myScore = isChallenger ? challenge.challenger_score : challenge.challenged_score;
  const theirScore = isChallenger ? challenge.challenged_score : challenge.challenger_score;
  const finalizedDate = challenge.finalized_at ? new Date(challenge.finalized_at).toLocaleString() : null;

  const total = isScenario ? scenarios.length : questions.length;
  const current = isScenario ? scenarios[currentIdx] : questions[currentIdx];
  const titleLabel = isScenario ? "Scenario Showdown" : challenge.challenge_type === "topic_battle" ? "Topic Battle" : challenge.challenge_type === "daily_duel" ? "Daily Duel" : "Quiz Duel";

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/80 backdrop-blur-md sticky top-0 z-50">
        <div className="container max-w-3xl mx-auto flex items-center gap-3 py-4 px-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/challenges")}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="font-heading font-bold text-lg text-foreground flex items-center gap-2">
            {isScenario && <Lightbulb className="w-4 h-4 text-accent" />}
            {titleLabel}
          </h1>
          <span className="ml-auto text-xs text-muted-foreground">{answeredIds.size} / {total}</span>
        </div>
      </header>

      <main className="container max-w-3xl mx-auto py-6 px-4">
        {finalState ? (
          <div className="glass-card p-8 text-center space-y-5">
            <Trophy className={cn(
              "w-14 h-14 mx-auto",
              iWon && "text-primary",
              iLost && "text-destructive/70",
              (isTie || isExpired) && "text-muted-foreground",
            )} />
            <div>
              <span className={cn(
                "inline-block text-[11px] uppercase tracking-[0.18em] font-bold px-3 py-1 rounded-full border",
                iWon && "bg-primary/10 text-primary border-primary/30",
                iLost && "bg-destructive/10 text-destructive border-destructive/30",
                isTie && "bg-muted/40 text-muted-foreground border-border",
                isExpired && "bg-muted/40 text-muted-foreground border-border",
              )}>
                {iWon ? "You Won" : iLost ? "You Lost" : isExpired ? "Expired" : "Tie"}
              </span>
              <h2 className="font-heading font-bold text-3xl text-foreground mt-3 tabular-nums">
                {myScore} <span className="text-muted-foreground font-normal mx-2">–</span> {theirScore}
              </h2>
              <p className="text-sm text-muted-foreground mt-2">
                {iWon && `You answered more correctly (${myScore} vs ${theirScore}).`}
                {iLost && `Opponent answered more correctly (${theirScore} vs ${myScore}).`}
                {isTie && `Both players answered ${myScore} correctly. Tied results stay tied — no automatic tiebreaker.`}
                {isExpired && `The duel expired before either side completed.`}
              </p>
              {finalizedDate && (
                <p className="text-[11px] text-muted-foreground/70 mt-2">{titleLabel} · {finalizedDate}</p>
              )}
            </div>
            <Button onClick={() => navigate("/challenges")}>Back to Challenge Center</Button>
          </div>
        ) : declinedOrCanceled ? (
          <div className="glass-card p-8 text-center space-y-4">
            <X className="w-8 h-8 mx-auto text-muted-foreground" />
            <h2 className="font-heading font-bold text-xl text-foreground">Challenge Closed</h2>
            <p className="text-sm text-muted-foreground">This challenge is no longer active.</p>
            <Button variant="outline" onClick={() => navigate("/challenges")}>Back to Challenge Center</Button>
          </div>
        ) : isPendingAcceptance ? (
          <div className="glass-card p-8 text-center space-y-4">
            <Hourglass className="w-8 h-8 mx-auto text-muted-foreground" />
            <h2 className="font-heading font-bold text-xl text-foreground">
              {isChallenger ? "Awaiting Opponent" : "Challenge Pending"}
            </h2>
            <p className="text-sm text-muted-foreground">
              {isChallenger
                ? "Your challenge has been sent. We'll notify you the moment your opponent accepts so you can begin."
                : "Head back to the Challenge Center to accept this challenge and start playing."}
            </p>
            <Button onClick={() => navigate("/challenges")}>Back to Challenge Center</Button>
          </div>
        ) : myDone ? (
          <div className="glass-card p-8 text-center space-y-4">
            <Loader2 className="w-8 h-8 mx-auto text-primary animate-spin" />
            <h2 className="font-heading font-bold text-xl text-foreground">Waiting for Opponent to Finish</h2>
            <p className="text-sm text-muted-foreground">Your turn is complete. We'll resolve the challenge the moment your opponent finishes — or when time expires.</p>
            <Button variant="outline" onClick={() => navigate("/challenges")}>Back to Challenge Center</Button>
          </div>
        ) : !current ? (
          <div className="glass-card p-8 text-center text-muted-foreground">No content available.</div>
        ) : isScenario ? (
          <div className="space-y-4">
            <div className="glass-card p-6">
              <p className="text-xs uppercase tracking-wider text-accent mb-2">Scenario {currentIdx + 1} of {total} · {(current as Scenario).topic}</p>
              <p className="text-foreground leading-relaxed">{(current as Scenario).scenario_text}</p>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {(["A","B","C","D"] as const).map(letter => {
                const text = (current)[`option_${letter.toLowerCase()}`];
                if (!text) return null;
                const isBest = lastResult && lastResult.bestAnswer === letter;
                return (
                  <button
                    key={letter}
                    disabled={submitting || !!lastResult}
                    onClick={() => submitScenarioAnswer(letter)}
                    className={cn(
                      "glass-card p-4 text-left transition-all border-2 disabled:cursor-not-allowed",
                      "border-border hover:border-accent/40",
                      lastResult && isBest && "border-primary bg-primary/10",
                      lastResult && !isBest && "opacity-50",
                    )}
                  >
                    <span className="font-bold text-accent mr-2">{letter}.</span>
                    <span className="text-foreground">{text}</span>
                  </button>
                );
              })}
            </div>
            {lastResult && (
              <div className="glass-card p-4 space-y-2 border-2 border-primary/30">
                <div className={cn("flex items-center gap-2 text-sm font-semibold",
                  lastResult.correct ? "text-primary" : "text-destructive")}>
                  {lastResult.correct ? <><Check className="w-4 h-4" /> Strong choice</> : <><X className="w-4 h-4" /> Not the strongest move</>}
                </div>
                {lastResult.whyBest && (
                  <p className="text-sm text-muted-foreground">
                    <span className="text-foreground font-medium">Why {lastResult.bestAnswer} is best: </span>
                    {lastResult.whyBest}
                  </p>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="glass-card p-6">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Question {currentIdx + 1} of {total}</p>
              <h2 className="font-heading text-xl text-foreground">{(current as Question).question}</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(["a","b","c","d"] as const).map(letter => {
                const text = (current)[`option_${letter}`];
                if (!text) return null;
                const isSelectedAnswer = lastResult && lastResult.correct;
                return (
                  <button
                    key={letter}
                    disabled={submitting || !!lastResult}
                    onClick={() => submitQuestionAnswer(letter.toUpperCase())}
                    className={cn(
                      "glass-card p-4 text-left transition-all border-2 disabled:cursor-not-allowed",
                      "border-border hover:border-primary/40",
                      lastResult && "opacity-60",
                    )}
                  >
                    <span className="font-bold text-primary mr-2">{letter.toUpperCase()}.</span>
                    <span className="text-foreground">{text}</span>
                  </button>
                );
              })}
            </div>
            {lastResult && (
              <div className={cn("glass-card p-3 text-center text-sm font-semibold flex items-center justify-center gap-2",
                lastResult.correct ? "text-primary" : "text-destructive")}>
                {lastResult.correct ? <><Check className="w-4 h-4" /> Correct</> : <><X className="w-4 h-4" /> Incorrect</>}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default ChallengeDuel;
